'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';

// ── Shared diamond SVG ──────────────────────────────────────────────────────
function GemIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 28 28"
      className={className}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="gem-g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      <polygon points="14,2 2,11 6,26 22,26 26,11" fill="url(#gem-g)" />
    </svg>
  );
}

// ── Trusted-by logos (text placeholders) ────────────────────────────────────
const TRUSTED = ['GIA', 'Rapaport', 'TGJTA', 'JBT', 'WJA', 'AGS'];

// ── Value propositions ───────────────────────────────────────────────────────
const VALUE_PROPS = [
  {
    icon: '💎',
    title: 'Inventory Intelligence',
    desc: 'Track every stone by cut, carat, color, clarity, and certificate number. QR scanning, GIA verification, and aging alerts built in.',
    bullets: [
      'Unlimited SKUs with 4C attributes',
      'QR code generation & scanning',
      'GIA certificate verification',
      'Automatic aging & hold alerts',
    ],
    href: '/features#inventory',
    accent: 'from-violet-500/20 to-violet-600/5 border-violet-500/30',
    iconBg: 'bg-violet-500/15 border-violet-500/30',
  },
  {
    icon: '📈',
    title: 'Precision Pricing',
    desc: 'Live Rapaport integration with custom markup rules. Set price by shape, quality tier, or client relationship — automatically.',
    bullets: [
      'Live Rapaport price feed',
      'Custom markup rule engine',
      'Per-client pricing tiers',
      'Profit margin calculator',
    ],
    href: '/features#pricing',
    accent: 'from-amber-500/20 to-amber-600/5 border-amber-500/30',
    iconBg: 'bg-amber-500/15 border-amber-500/30',
  },
  {
    icon: '🤝',
    title: 'Relationship CRM',
    desc: 'Manage wishlists, follow-up tasks, and deal history for every buyer and supplier. Never let a hot lead go cold.',
    bullets: [
      'Client & supplier profiles',
      'Wishlist & preference tracking',
      'Automated follow-up reminders',
      'Deal pipeline & close rates',
    ],
    href: '/features#crm',
    accent: 'from-indigo-500/20 to-indigo-600/5 border-indigo-500/30',
    iconBg: 'bg-indigo-500/15 border-indigo-500/30',
  },
];

// ── How it works ─────────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Import Your Inventory',
    desc: 'Upload a CSV, scan QR codes, or add stones manually. Attach GIA reports and photos in seconds.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10 border-violet-500/20',
  },
  {
    step: '02',
    title: 'Set Your Pricing Rules',
    desc: 'Connect to live Rapaport data. Define markup rules by shape, quality, and client tier — prices update automatically.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20',
  },
  {
    step: '03',
    title: 'Close Deals Faster',
    desc: 'Share live inventory links with buyers, track memos and holds, and convert pipeline deals with one click.',
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10 border-indigo-500/20',
  },
];

// ── Testimonials ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote:
      'Glanz CRM cut our memo tracking time by 80%. We used to lose stones in spreadsheets — now every diamond has a real-time status.',
    name: 'Somchai P.',
    role: 'Owner, Silom Diamond Co.',
    type: 'Retailer',
    stars: 5,
  },
  {
    quote:
      "The Rapaport integration alone is worth the subscription. Pricing 200+ stones used to take half a day. Now it's 10 minutes.",
    name: 'David L.',
    role: 'Director, Bangkok Gem Wholesale',
    type: 'Wholesaler',
    stars: 5,
  },
  {
    quote:
      'As an appraiser, the GIA verification and certificate management features are exactly what I needed. Professional and fast.',
    name: 'Nattaporn K.',
    role: 'Principal Appraiser, GIA Alumni',
    type: 'Appraiser',
    stars: 5,
  },
];

// ── Solutions grid ────────────────────────────────────────────────────────────
const SOLUTIONS = [
  {
    icon: '🏪',
    title: 'Retailers',
    desc: 'Showroom inventory, client wishlists, and POS-ready sales flow.',
    href: '/solutions#retailers',
  },
  {
    icon: '🏭',
    title: 'Wholesalers',
    desc: 'Bulk memo management, supplier payables, and volume pricing.',
    href: '/solutions#wholesalers',
  },
  {
    icon: '🔬',
    title: 'Appraisers',
    desc: 'GIA-linked appraisal reports, certificate archive, and compliance logs.',
    href: '/solutions#appraisers',
  },
  {
    icon: '🌐',
    title: 'Multi-Location',
    desc: 'Unified inventory across branches with role-based access per location.',
    href: '/solutions#multi-location',
  },
];

// ── Pricing teaser ────────────────────────────────────────────────────────────
const PRICING_TIERS = [
  {
    name: 'Starter',
    price: 99,
    tagline: 'Solo traders',
    highlight: false,
    color: 'border-violet-500/30 text-violet-400',
  },
  {
    name: 'Professional',
    price: 249,
    tagline: 'Growing teams',
    highlight: true,
    color: 'border-amber-500/50 text-amber-400',
  },
  {
    name: 'Enterprise',
    price: 599,
    tagline: 'Large operations',
    highlight: false,
    color: 'border-indigo-500/30 text-indigo-400',
  },
];

// ── Feature tabs (static — defined outside component) ────────────────────────
const FEATURE_TABS = {
  inventory: {
    label: 'Inventory',
    checks: [
      '4C attribute tracking',
      'QR code scanning',
      'GIA certificate link',
      'Aging & hold alerts',
      'Bulk CSV import',
      'Photo gallery per stone',
    ],
  },
  pricing: {
    label: 'Pricing',
    checks: [
      'Live Rapaport feed',
      'Custom markup rules',
      'Per-client tiers',
      'Profit margin view',
      'Currency toggle (THB/USD)',
      'Discount approval flow',
    ],
  },
  crm: {
    label: 'CRM',
    checks: [
      'Client & supplier profiles',
      'Wishlist management',
      'Follow-up task queue',
      'Deal pipeline stages',
      'Email automation',
      'Activity timeline',
    ],
  },
  appraisal: {
    label: 'Appraisal',
    checks: [
      'Appraisal report builder',
      'GIA verification API',
      'Certificate archive',
      'Compliance audit log',
      'Digital signature',
      'PDF export',
    ],
  },
};

// ── Tour step bullets (static lookup) ────────────────────────────────────────
const TOUR_BULLETS: Record<number, string[]> = {
  0: ['Scan QR or enter cert number', 'Auto-fill from GIA database', 'Attach photos in one tap'],
  1: [
    'Live Rapaport feed integration',
    'Per-shape & per-tier markup rules',
    'Instant margin calculator',
  ],
  2: ['One-click memo issuance', '48-hour return alerts', 'Full memo history per client'],
  3: [
    'Wishlist & preference tracking',
    'Deal pipeline with stage tracking',
    'Automated follow-up reminders',
  ],
  4: ['One-click deal conversion', 'Real-time P&L dashboard', 'PDF invoice generation'],
};

// ── Product Tour steps ────────────────────────────────────────────────────────
const TOUR_STEPS = [
  {
    id: 'add-stone',
    label: 'Add a Stone',
    icon: '💎',
    title: 'Add any diamond in under 30 seconds',
    desc: 'Scan a QR code or type the certificate number. Glanz auto-fills cut, carat, color, and clarity from the GIA database — no manual entry.',
    accent: 'violet',
    accentHex: '#7c3aed',
    mockLines: [
      { label: 'Stone ID', value: 'D-4921', color: 'text-violet-400' },
      { label: 'Shape', value: 'Round Brilliant', color: 'text-white' },
      { label: 'Carat', value: '1.52 ct', color: 'text-white' },
      { label: 'Color / Clarity', value: 'F / VS1', color: 'text-white' },
      { label: 'GIA Certificate', value: '2486123456 ✓', color: 'text-emerald-400' },
      { label: 'Status', value: 'Available', color: 'text-emerald-400' },
    ],
    badge: {
      text: 'GIA Verified',
      color: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
    },
  },
  {
    id: 'set-price',
    label: 'Set Pricing',
    icon: '📈',
    title: 'Live Rapaport pricing, your markup rules',
    desc: 'Connect once to Rapaport. Define markup rules by shape, quality tier, or client. Prices recalculate automatically every time the feed updates.',
    accent: 'amber',
    accentHex: '#d97706',
    mockLines: [
      { label: 'Rapaport Base', value: '$5,200 / ct', color: 'text-amber-400' },
      { label: 'Markup Rule', value: 'Round F-VS1 → +18%', color: 'text-white' },
      { label: 'Calculated Price', value: '$9,282', color: 'text-white' },
      { label: 'Client Tier', value: 'Wholesale (−5%)', color: 'text-white' },
      { label: 'Final Quote', value: '$8,818', color: 'text-amber-300' },
      { label: 'Margin', value: '22.4%', color: 'text-emerald-400' },
    ],
    badge: { text: 'Live Feed', color: 'bg-amber-500/15 border-amber-500/30 text-amber-400' },
  },
  {
    id: 'memo',
    label: 'Issue a Memo',
    icon: '📋',
    title: 'Memo out stones with one click',
    desc: 'Select stones, pick a client, set a return date. Glanz tracks every memo in real time — and alerts you 48 hours before anything is due back.',
    accent: 'indigo',
    accentHex: '#4f46e5',
    mockLines: [
      { label: 'Memo #', value: 'M-0291', color: 'text-indigo-400' },
      { label: 'Client', value: 'Silom Diamond Co.', color: 'text-white' },
      { label: 'Stones', value: '3 items · $31,400', color: 'text-white' },
      { label: 'Issued', value: 'Today, 10:32 AM', color: 'text-white' },
      { label: 'Return Due', value: '14 Aug 2026', color: 'text-amber-400' },
      { label: 'Status', value: 'Active', color: 'text-emerald-400' },
    ],
    badge: {
      text: 'Auto-Reminder',
      color: 'bg-indigo-500/15 border-indigo-500/30 text-indigo-400',
    },
  },
  {
    id: 'crm',
    label: 'Track Clients',
    icon: '🤝',
    title: 'Every client relationship, in one place',
    desc: 'Wishlists, purchase history, follow-up tasks, and deal stage — all linked to each contact. Never forget a hot lead or a promised callback.',
    accent: 'rose',
    accentHex: '#e11d48',
    mockLines: [
      { label: 'Client', value: 'Nattaporn K.', color: 'text-white' },
      { label: 'Wishlist', value: '2ct+ Round, D-E, VVS', color: 'text-white' },
      { label: 'Last Purchase', value: '$22,800 · 3 weeks ago', color: 'text-white' },
      { label: 'Pipeline Stage', value: 'Negotiation', color: 'text-amber-400' },
      { label: 'Follow-up', value: 'Call tomorrow 2 PM', color: 'text-rose-400' },
      { label: 'Lifetime Value', value: '$148,200', color: 'text-emerald-400' },
    ],
    badge: { text: 'Smart Follow-up', color: 'bg-rose-500/15 border-rose-500/30 text-rose-400' },
  },
  {
    id: 'close',
    label: 'Close & Report',
    icon: '✅',
    title: 'Close deals and see your numbers instantly',
    desc: 'Convert a pipeline deal to a sale in one click. Profit & loss, inventory aging, and revenue charts update in real time — no spreadsheets needed.',
    accent: 'emerald',
    accentHex: '#059669',
    mockLines: [
      { label: 'Sale #', value: 'S-1042', color: 'text-emerald-400' },
      { label: 'Stone', value: 'Round 1.52ct F/VS1', color: 'text-white' },
      { label: 'Sale Price', value: '$8,818', color: 'text-white' },
      { label: 'Cost Basis', value: '$6,890', color: 'text-white' },
      { label: 'Gross Profit', value: '$1,928 (21.9%)', color: 'text-emerald-400' },
      { label: 'Invoice', value: 'PDF generated ✓', color: 'text-emerald-400' },
    ],
    badge: {
      text: 'Instant P&L',
      color: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
    },
  },
];

// ── Lead capture form ─────────────────────────────────────────────────────────
function LeadCaptureForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', city: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !consent) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed');
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
          <svg
            className="w-7 h-7 text-emerald-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-base font-semibold text-white">You&apos;re on the list!</p>
        <p className="text-sm text-[#9ca3af] mt-1">We&apos;ll be in touch within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lead-name" className="block text-xs font-medium text-[#9ca3af] mb-1.5">
            Full Name *
          </label>
          <input
            id="lead-name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors"
            placeholder="John Smith"
          />
        </div>
        <div>
          <label htmlFor="lead-email" className="block text-xs font-medium text-[#9ca3af] mb-1.5">
            Email *
          </label>
          <input
            id="lead-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label htmlFor="lead-phone" className="block text-xs font-medium text-[#9ca3af] mb-1.5">
            Phone / LINE ID
          </label>
          <input
            id="lead-phone"
            type="text"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors"
            placeholder="+66 81 234 5678"
          />
        </div>
        <div>
          <label
            htmlFor="lead-business"
            className="block text-xs font-medium text-[#9ca3af] mb-1.5"
          >
            Business Name
          </label>
          <input
            id="lead-business"
            type="text"
            value={form.business}
            onChange={(e) => setForm((f) => ({ ...f, business: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors"
            placeholder="Siam Diamond Co."
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lead-city" className="block text-xs font-medium text-[#9ca3af] mb-1.5">
            City
          </label>
          <input
            id="lead-city"
            type="text"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors"
            placeholder="Bangkok"
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <label
        htmlFor="lead-consent"
        className="flex items-start gap-2 text-xs text-[#9ca3af] cursor-pointer"
      >
        <input
          id="lead-consent"
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-violet-500"
        />
        <span>
          I agree to the{' '}
          <Link href="/privacy" className="text-violet-400 hover:text-violet-300 underline">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link href="/legal/terms" className="text-violet-400 hover:text-violet-300 underline">
            Terms of Service
          </Link>
          .
        </span>
      </label>
      <button
        type="submit"
        disabled={loading || !consent}
        className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-60 text-white font-semibold text-sm transition-all"
      >
        {loading ? 'Sending...' : 'Start Free Trial'}
      </button>
      <p className="text-xs text-center text-[#8b93a3]">No credit card required · Cancel anytime</p>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MarketingHome() {
  const [activeTab, setActiveTab] = useState<'inventory' | 'pricing' | 'crm' | 'appraisal'>(
    'inventory'
  );
  const [tourStep, setTourStep] = useState(0);
  const [tourAnimating, setTourAnimating] = useState(false);
  const leadRef = useRef<HTMLDivElement>(null);
  const tourRef = useRef<HTMLDivElement>(null);

  // Auto-advance tour every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setTourAnimating(true);
      setTimeout(() => {
        setTourStep((s) => (s + 1) % TOUR_STEPS.length);
        setTourAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToStep = (idx: number) => {
    if (idx === tourStep) return;
    setTourAnimating(true);
    setTimeout(() => {
      setTourStep(idx);
      setTourAnimating(false);
    }, 250);
  };

  const scrollToLead = () =>
    leadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const step = TOUR_STEPS[tourStep];

  return (
    <div
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        {/* ── HERO ── */}
        <section className="relative pt-28 pb-20 px-4 sm:px-6 overflow-hidden">
          {/* Atmospheric glows */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-gradient-to-b from-amber-500/6 via-violet-600/8 to-transparent blur-[120px]" />
            <div className="absolute top-1/3 right-0 w-96 h-96 rounded-full bg-indigo-600/5 blur-[80px]" />
            {/* Floating gem particles */}
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-0.5 h-0.5 rounded-full bg-amber-400/50"
                style={{
                  left: `${10 + i * 11}%`,
                  top: `${15 + (i % 4) * 18}%`,
                  animation: `float-gem ${2.5 + i * 0.4}s ease-in-out infinite alternate`,
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: copy */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/8 text-amber-300 text-xs font-medium mb-7">
                  <GemIcon />
                  <span>Diamond Merchant CRM — Built for Thailand</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-white leading-[1.1] mb-6 tracking-tight">
                  Run Your Diamond Business
                  <br />
                  <span className="text-gold-gradient">Without the Chaos</span>
                </h1>

                <p className="text-[#9ca3af] text-lg leading-relaxed mb-8 max-w-lg">
                  Inventory, pricing, CRM, and appraisal — unified in one platform designed for
                  diamond merchants in Bangkok and beyond.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <button
                    onClick={scrollToLead}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/25 transition-all hover:-translate-y-0.5 hover:shadow-amber-500/40"
                  >
                    Start Free Trial
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </button>
                  <Link
                    href="/demo"
                    className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 text-white font-medium text-sm transition-all hover:-translate-y-0.5"
                  >
                    See Interactive Demo
                  </Link>
                </div>

                <p className="text-xs text-[#8b93a3]">
                  No credit card required · 14-day free trial · Cancel anytime
                </p>
              </div>

              {/* Right: stylized platform mockup */}
              <div className="relative hidden lg:block">
                <div className="relative rounded-2xl border border-white/10 bg-[#111827] overflow-hidden shadow-2xl shadow-black/60">
                  {/* Mock dashboard header */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#0d1120]">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                    </div>
                    <div className="flex-1 mx-4 h-5 rounded-md bg-white/5 flex items-center px-2">
                      <span className="text-[10px] text-[#8b93a3]">app.glanzcrm.com/dashboard</span>
                    </div>
                  </div>
                  {/* Mock KPI row */}
                  <div className="grid grid-cols-3 gap-3 p-4">
                    {[
                      {
                        label: 'Total Inventory',
                        val: '1,247',
                        sub: '+12 this week',
                        color: 'text-violet-400',
                      },
                      {
                        label: 'Active Memos',
                        val: '38',
                        sub: '3 expiring soon',
                        color: 'text-amber-400',
                      },
                      {
                        label: 'Pipeline Value',
                        val: '$2.4M',
                        sub: '↑ 18% vs last month',
                        color: 'text-indigo-400',
                      },
                    ].map((kpi) => (
                      <div
                        key={kpi.label}
                        className="rounded-xl bg-[#0d1120] border border-white/5 p-3"
                      >
                        <p className="text-[10px] text-[#9ca3af] mb-1">{kpi.label}</p>
                        <p className={`text-lg font-bold ${kpi.color}`}>{kpi.val}</p>
                        <p className="text-[9px] text-[#8b93a3] mt-0.5">{kpi.sub}</p>
                      </div>
                    ))}
                  </div>
                  {/* Mock inventory table */}
                  <div className="px-4 pb-4">
                    <div className="rounded-xl bg-[#0d1120] border border-white/5 overflow-hidden">
                      <div className="grid grid-cols-4 gap-2 px-3 py-2 border-b border-white/5">
                        {['Stone ID', 'Shape / Carat', 'Status', 'Price'].map((h) => (
                          <span
                            key={h}
                            className="text-[9px] font-semibold text-[#8b93a3] uppercase tracking-wide"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                      {[
                        {
                          id: 'D-4821',
                          shape: 'Round 1.52ct',
                          status: 'Available',
                          price: '$8,400',
                          statusColor: 'text-emerald-400',
                        },
                        {
                          id: 'D-4822',
                          shape: 'Princess 2.01ct',
                          status: 'On Memo',
                          price: '$14,200',
                          statusColor: 'text-amber-400',
                        },
                        {
                          id: 'D-4823',
                          shape: 'Oval 0.87ct',
                          status: 'On Hold',
                          price: '$3,900',
                          statusColor: 'text-blue-400',
                        },
                        {
                          id: 'D-4824',
                          shape: 'Cushion 3.15ct',
                          status: 'Available',
                          price: '$22,800',
                          statusColor: 'text-emerald-400',
                        },
                      ].map((row) => (
                        <div
                          key={row.id}
                          className="grid grid-cols-4 gap-2 px-3 py-2 border-b border-white/3 hover:bg-white/2"
                        >
                          <span className="text-[10px] text-violet-400 font-mono">{row.id}</span>
                          <span className="text-[10px] text-[#9ca3af]">{row.shape}</span>
                          <span className={`text-[10px] font-medium ${row.statusColor}`}>
                            {row.status}
                          </span>
                          <span className="text-[10px] text-white font-semibold">{row.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 rounded-xl border border-amber-500/30 bg-[#111827] px-4 py-3 shadow-xl">
                  <p className="text-xs font-semibold text-amber-400">2M+ Diamonds Tracked</p>
                  <p className="text-[10px] text-[#9ca3af]">across 500+ merchants</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TRUSTED BY ── */}
        <section className="py-10 px-4 sm:px-6 border-y border-white/5">
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-xs text-[#8b93a3] uppercase tracking-widest mb-6">
              Trusted by merchants across Thailand & Southeast Asia
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8">
              {TRUSTED.map((name) => (
                <span
                  key={name}
                  className="text-sm font-bold text-[#8b93a3] tracking-widest uppercase"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── VALUE PROPOSITIONS ── */}
        <section className="py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Three Pillars of Diamond Business Mastery
              </h2>
              <p className="text-[#9ca3af] max-w-xl mx-auto">
                Every feature in Glanz CRM maps to a real problem diamond merchants face every day.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {VALUE_PROPS.map((vp) => (
                <div
                  key={vp.title}
                  className={`group rounded-2xl border bg-gradient-to-b ${vp.accent} p-6 hover:-translate-y-1 transition-all duration-300`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl border ${vp.iconBg} flex items-center justify-center text-2xl mb-5`}
                  >
                    {vp.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{vp.title}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed mb-5">{vp.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {vp.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-xs text-[#d1d5db]">
                        <svg
                          className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href={vp.href}
                    className="text-xs font-semibold text-white/60 hover:text-white transition-colors flex items-center gap-1"
                  >
                    Explore module
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-20 px-4 sm:px-6 bg-[#0d1120]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Up and Running in 3 Steps
              </h2>
              <p className="text-[#9ca3af]">
                No IT team required. Most merchants are live within one business day.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 relative">
              {/* Connector lines */}
              <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-px bg-gradient-to-r from-violet-500/30 via-amber-500/30 to-indigo-500/30" />

              {HOW_IT_WORKS.map((s) => (
                <div key={s.step} className={`relative rounded-2xl border ${s.bg} p-7`}>
                  <div className={`text-2xl font-black ${s.color} mb-4`}>{s.step}</div>
                  <h3 className="text-base font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURE SHOWCASE (tabbed) ── */}
        <section className="py-20 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Every Module, Deeply Integrated
              </h2>
              <p className="text-[#9ca3af]">
                Switch between modules — data flows seamlessly between them all.
              </p>
            </div>

            {/* Tab switcher */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {(Object.keys(FEATURE_TABS) as Array<keyof typeof FEATURE_TABS>).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab
                      ? 'bg-white/10 text-white border border-white/20'
                      : 'text-[#9ca3af] hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  {FEATURE_TABS[tab].label}
                </button>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#111827] overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Screenshot placeholder */}
                <div className="bg-[#0d1120] border-b md:border-b-0 md:border-r border-white/5 p-8 flex items-center justify-center min-h-[280px]">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-indigo-600/20 border border-violet-500/30 flex items-center justify-center text-3xl mx-auto mb-4">
                      {activeTab === 'inventory'
                        ? '💎'
                        : activeTab === 'pricing'
                          ? '📈'
                          : activeTab === 'crm'
                            ? '🤝'
                            : '🔬'}
                    </div>
                    <p className="text-sm text-[#8b93a3]">{FEATURE_TABS[activeTab].label} Module</p>
                    <p className="text-xs text-[#8b93a3] mt-1">Screenshot coming soon</p>
                  </div>
                </div>
                {/* Feature checklist */}
                <div className="p-8">
                  <h3 className="text-lg font-bold text-white mb-5">
                    {FEATURE_TABS[activeTab].label} Features
                  </h3>
                  <ul className="space-y-3">
                    {FEATURE_TABS[activeTab].checks.map((check) => (
                      <li key={check} className="flex items-center gap-3 text-sm text-[#d1d5db]">
                        <div className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-3 h-3 text-emerald-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        {check}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href={`/features#${activeTab}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    See full {FEATURE_TABS[activeTab].label} docs
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRODUCT TOUR ── */}
        <section ref={tourRef} className="py-24 px-4 sm:px-6 bg-[#080c18] relative overflow-hidden">
          {/* Background atmosphere */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full blur-[140px] transition-all duration-1000"
              style={{
                background: `radial-gradient(ellipse, ${step.accentHex}12 0%, transparent 70%)`,
              }}
            />
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* Header */}
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs text-[#9ca3af] mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Interactive Product Tour
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                See Glanz CRM in Action
              </h2>
              <p className="text-[#9ca3af] max-w-lg mx-auto">
                Walk through a real diamond deal — from adding a stone to closing the sale and
                reading your P&amp;L.
              </p>
            </div>

            {/* Step pills */}
            <div className="flex flex-wrap gap-2 justify-center mb-10">
              {TOUR_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => goToStep(i)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${
                    i === tourStep
                      ? 'bg-white/12 text-white border border-white/25 shadow-lg'
                      : 'text-[#9ca3af] border border-transparent hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span>{s.icon}</span>
                  <span>{s.label}</span>
                  {i === tourStep && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  )}
                </button>
              ))}
            </div>

            {/* Main tour panel */}
            <div
              className="rounded-2xl border border-white/10 bg-[#0d1120] overflow-hidden shadow-2xl shadow-black/60 transition-all duration-300"
              style={{
                opacity: tourAnimating ? 0 : 1,
                transform: tourAnimating ? 'translateY(8px)' : 'translateY(0)',
              }}
            >
              <div className="grid lg:grid-cols-2">
                {/* Left: mock UI panel */}
                <div className="border-b lg:border-b-0 lg:border-r border-white/5 p-8 flex flex-col">
                  {/* Mock window chrome */}
                  <div className="flex items-center gap-2 mb-5">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-500/50" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
                    </div>
                    <div className="flex-1 h-5 rounded-md bg-white/5 flex items-center px-2 ml-2">
                      <span className="text-[10px] text-[#8b93a3]">
                        {'Glanz CRM · ' + step.label}
                      </span>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${step.badge.color}`}
                    >
                      {step.badge.text}
                    </span>
                  </div>

                  {/* Step icon + title */}
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-xl border"
                      style={{
                        background: `${step.accentHex}18`,
                        borderColor: `${step.accentHex}40`,
                      }}
                    >
                      {step.icon}
                    </div>
                    <div>
                      <p className="text-xs text-[#8b93a3] uppercase tracking-wider mb-0.5">
                        {'Step ' + (tourStep + 1) + ' of ' + TOUR_STEPS.length}
                      </p>
                      <p className="text-sm font-bold text-white">{step.label}</p>
                    </div>
                  </div>

                  {/* Mock data rows */}
                  <div className="rounded-xl bg-[#080c18] border border-white/5 overflow-hidden flex-1">
                    {step.mockLines.map((line, li) => (
                      <div
                        key={line.label}
                        className="flex items-center justify-between px-4 py-2.5 border-b border-white/4 last:border-b-0"
                        style={{
                          animation: `tour-row-in 0.35s ease-out both`,
                          animationDelay: `${li * 55}ms`,
                        }}
                      >
                        <span className="text-[11px] text-[#8b93a3]">{line.label}</span>
                        <span className={`text-[11px] font-semibold font-mono ${line.color}`}>
                          {line.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-5">
                    <div className="flex justify-between text-[10px] text-[#8b93a3] mb-1.5">
                      <span>Tour progress</span>
                      <span>
                        {tourStep + 1} / {TOUR_STEPS.length}
                      </span>
                    </div>
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${((tourStep + 1) / TOUR_STEPS.length) * 100}%`,
                          background: `linear-gradient(90deg, ${step.accentHex}, ${step.accentHex}cc)`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Right: description + nav */}
                <div className="p-8 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 leading-snug">
                      {step.title}
                    </h3>
                    <p className="text-[#9ca3af] leading-relaxed text-sm mb-8">{step.desc}</p>

                    {/* Feature bullets for this step */}
                    <div className="space-y-3 mb-8">
                      {(TOUR_BULLETS[tourStep] ?? []).map((bullet) => (
                        <div
                          key={bullet}
                          className="flex items-center gap-2.5 text-sm text-[#d1d5db]"
                        >
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `${step.accentHex}20`,
                              border: `1px solid ${step.accentHex}50`,
                            }}
                          >
                            <svg
                              className="w-2.5 h-2.5"
                              style={{ color: step.accentHex }}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          {bullet}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        goToStep((tourStep - 1 + TOUR_STEPS.length) % TOUR_STEPS.length)
                      }
                      className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all"
                      aria-label="Previous step"
                    >
                      <svg
                        className="w-4 h-4 text-[#9ca3af]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>

                    {/* Dot indicators — the button carries the 24px hit area
                        (WCAG 2.5.8 Target Size, Minimum) while the inner span
                        keeps the visual 6px dot, so the control looks the same
                        but is usable with a finger or a shaky pointer. */}
                    <div className="flex items-center flex-1 justify-center">
                      {TOUR_STEPS.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => goToStep(i)}
                          className="w-6 h-6 flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                          aria-current={i === tourStep ? 'step' : undefined}
                          aria-label={`Go to step ${i + 1}`}
                        >
                          <span
                            className="block rounded-full transition-all duration-300"
                            style={{
                              width: i === tourStep ? '20px' : '6px',
                              height: '6px',
                              background: i === tourStep ? step.accentHex : '#8b93a3',
                            }}
                          />
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => goToStep((tourStep + 1) % TOUR_STEPS.length)}
                      className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all"
                      aria-label="Next step"
                    >
                      <svg
                        className="w-4 h-4 text-[#9ca3af]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/5">
                    <Link
                      href="/demo"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-xs shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5"
                    >
                      Try it live — Book a Demo
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="py-20 px-4 sm:px-6 bg-[#0d1120]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Merchants Who Made the Switch
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t) => (
                <div
                  key={t.name}
                  className="rounded-2xl border border-white/8 bg-[#111827] p-6 flex flex-col"
                >
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(t.stars)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-4 h-4 text-amber-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-[#d1d5db] leading-relaxed flex-1 mb-5 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500/30 to-indigo-600/30 border border-violet-500/30 flex items-center justify-center text-sm font-bold text-violet-300">
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs text-[#9ca3af]">{t.role}</p>
                    </div>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#9ca3af]">
                      {t.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <section className="py-14 px-4 sm:px-6 bg-[#080c18] border-y border-white/5">
          <div className="max-w-4xl mx-auto grid grid-cols-3 gap-6 text-center">
            {[
              { num: '2M+', label: 'Diamonds Tracked' },
              { num: '500+', label: 'Active Merchants' },
              { num: '$1B+', label: 'Inventory Managed' },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  className="text-3xl sm:text-4xl font-black mb-1 bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #fbbf24, #f59e0b, #fde68a)' }}
                >
                  {stat.num}
                </div>
                <p className="text-xs text-[#9ca3af] uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── SOLUTIONS BY BUSINESS TYPE ── */}
        <section className="py-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Built for Every Type of Diamond Business
              </h2>
              <p className="text-[#9ca3af]">
                Whether you&apos;re a solo appraiser or a multi-branch wholesaler, Glanz CRM adapts
                to you.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SOLUTIONS.map((s) => (
                <Link
                  key={s.title}
                  href={s.href}
                  className="group rounded-2xl border border-white/8 bg-[#111827] p-6 hover:border-amber-500/30 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-3xl mb-4">{s.icon}</div>
                  <h3 className="text-base font-bold text-white mb-2 group-hover:text-amber-300 transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#9ca3af] leading-relaxed">{s.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs text-[#8b93a3] group-hover:text-amber-400 transition-colors">
                    Learn more
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRICING TEASER ── */}
        <section className="py-20 px-4 sm:px-6 bg-[#0d1120]">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-[#9ca3af]">
                No hidden fees. 14-day free trial on all plans. Cancel anytime.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className={`rounded-2xl border p-6 text-center relative ${
                    tier.highlight
                      ? 'border-amber-500/50 bg-gradient-to-b from-amber-500/8 to-[#111827]'
                      : 'border-white/8 bg-[#111827]'
                  }`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 rounded-full bg-amber-500 text-[#0a0e1a] text-xs font-bold shadow-lg">
                        ★ Most Popular
                      </span>
                    </div>
                  )}
                  <p className={`text-sm font-bold mb-1 ${tier.color.split(' ')[1]}`}>
                    {tier.name}
                  </p>
                  <p className="text-xs text-[#9ca3af] mb-4">{tier.tagline}</p>
                  <div className="text-3xl font-black text-white mb-1">${tier.price}</div>
                  <p className="text-xs text-[#8b93a3]">/month</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link
                href="/pricing"
                className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
              >
                Compare all features
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a] via-[#0d1120] to-[#0a0e1a]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-amber-500/6 blur-[100px]" />
          </div>
          <div className="max-w-2xl mx-auto text-center relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center text-2xl mx-auto mb-6">
              💎
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Modernize Your Diamond Business?
            </h2>
            <p className="text-[#9ca3af] mb-8 leading-relaxed">
              Join 500+ merchants in Bangkok and across Thailand already using Glanz CRM to track
              inventory, close deals, and grow revenue.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link
                href="/demo"
                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/25 transition-all hover:-translate-y-0.5"
              >
                Book a Demo
              </Link>
              <button
                onClick={scrollToLead}
                className="w-full sm:w-auto px-8 py-4 rounded-xl border border-white/15 bg-white/5 hover:bg-white/8 text-white font-medium text-sm transition-all hover:-translate-y-0.5"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>

        {/* ── LEAD CAPTURE ── */}
        <section ref={leadRef} className="py-20 px-4 sm:px-6 bg-[#0d1120] border-t border-white/5">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Start Your Free Trial</h2>
              <p className="text-sm text-[#9ca3af]">14 days free. No credit card required.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#111827] p-8">
              <LeadCaptureForm />
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />

      <style>{`
        @keyframes float-gem {
          from { transform: translateY(0px); opacity: 0.3; }
          to { transform: translateY(-10px); opacity: 0.7; }
        }
        @keyframes tour-row-in {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
